import { Button, Progress, TableColumnsType, TableColumnType } from 'antd';
import ScrollTable, { ScrollTableProps } from 'components/ScrollTable';
import { useIsStoreAliasFirst } from 'hooks/business/store/useStoreName';
import dayjs from 'lib/dayjs';
import { formatNumber } from 'utils/numberFormat';
import { useTranslate } from '@tolgee/react';

type RT = AeOverview.StoreSummary;

export interface OverviewTableProps extends ScrollTableProps<RT> {
  onCheckRecord: (record: RT) => void;
  onCheckMiss: (record: RT) => void;
}

function OverviewTable(props: OverviewTableProps) {
  const { onCheckRecord, onCheckMiss, dataSource = [], ...otherProps } = props;

  const { t } = useTranslate();

  const isStoreAliasFirst = useIsStoreAliasFirst('ALIEXPRESS', 'GB');

  const showProgress = dataSource.some(({ sendLimit }) => sendLimit >= 0);

  const progressColumn: TableColumnType<RT> = {
    key: 'progress',
    title: t('aeoverview.服务使用进度'),
    width: 210,
    render: (_, { sendLimit, sentNum, abandonedNum }) => {
      if (sendLimit < 0) {
        return '-';
      }

      const remain = Math.max(sendLimit - sentNum, 0);

      const percent = 100 - (remain / sendLimit) * 100;

      return (
        <div>
          <Progress
            showInfo={false}
            percent={Math.min(percent, 100)}
            strokeColor={(() => {
              if (percent >= 100) {
                return 'var(--ant-error-color)';
              }

              if (percent > 90) {
                return 'orange';
              }

              return 'rgb(51, 102, 255)';
            })()}
          />
          <div
            style={{
              fontSize: '12px',
              color: (() => {
                if (percent >= 100) {
                  return 'var(--ant-error-color)';
                }

                if (percent > 90) {
                  return 'orange';
                }

                return 'var(--fett-text-tertiary)';
              })(),
            }}
          >
            {remain === 0 && abandonedNum > 0
              ? t('aeoverview.本月已错失了x个买家的服务机会', {
                  x: formatNumber(abandonedNum, '0,0'),
                })
              : t('aeoverview.剩余x人', { x: formatNumber(remain, '0,0') })}
          </div>
        </div>
      );
    },
  };

  const columns: TableColumnsType<RT> = [
    {
      key: 'storeName',
      title: t('marketing.店铺名称'),
      width: 180,
      fixed: 'left',
      render: (_, { storeName, alias }) => (isStoreAliasFirst ? alias || storeName : storeName),
    },
    {
      key: 'version',
      title: t('aeoverview.服务版本'),
      width: 160,
      render: (_, { package: version, endTime }) => {
        return (
          <div>
            VIP{version}
            <div
              style={{
                fontSize: '12px',
                color: 'var(--fett-text-tertiary)',
              }}
            >
              {t('marketing.有效期')}：{dayjs(Number(endTime)).format('YYYY-MM-DD')}
            </div>
          </div>
        );
      },
    },
    {
      key: 'sentNum',
      dataIndex: 'sentNum',
      title: t('aeoverview.本月AI参与服务买家人数'),
      width: 190,
      render: (sentNum: RT['sentNum']) => formatNumber(sentNum, '0,0'),
    },
    ...(showProgress ? [progressColumn] : []),
    {
      key: 'operation',
      title: t('操作'),
      width: 320,
      render: (_, record) => {
        const { abandonedNum } = record;

        const checkRecord = (
          <Button type="link" onClick={() => onCheckRecord(record)} style={{ paddingLeft: 0 }}>
            {t('market.查看服务记录')}
          </Button>
        );

        if (abandonedNum <= 0) {
          return checkRecord;
        }

        return (
          <div>
            {checkRecord}
            <Button type="link" onClick={() => onCheckMiss(record)}>
              {t('aeoverview.查看错失服务机会记录')}
            </Button>
          </div>
        );
      },
    },
  ];

  return <ScrollTable rowKey="storeId" columns={columns} dataSource={dataSource} {...otherProps} />;
}

export default OverviewTable;
